// HashtagCategoryGrid.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import Loading from '../component/Loading.jsx';

import { ButtonId } from '../resource/trackingConstants.js';
import { searchType } from '../resource/searchConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color, textColor } from '../style/variables.js';
import media from '../style/media.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';

export const HashtagCategoryGrid = ({
  meId = null,
  category = null,
  nextPage = null,
  isNextPageFetching = false,
  hashtags = EMPTY_ARRAY,
  i18n,
  t,
  fetchFeeds = NULL_FUNCTION,
}) => {
  useEffect(() => {
    if (category) {
      fetchFeeds({ type: category, page: 1 });
    }
  }, [meId, category, fetchFeeds]);
  if (!hashtags.length) {
    return null;
  }
  return (
    <StyledHashtagCategoryGrid>
      <Header>
        <Title>
          {t(category, {
            ns: TranslationNamespace.FEED,
          })}
        </Title>
      </Header>
      <Body>
        <InfiniteScroller
          pageStart={1}
          hasMore={nextPage != null}
          loadMore={() => fetchFeeds({ type: category, page: nextPage })}
          isFetching={isNextPageFetching}
          threshold={100}
          useWindow={true}
        >
          <ScrollItem
            loader={
              <LoadingWrapper>
                <Loading />
              </LoadingWrapper>
            }
          >
            {hashtags.map((hashtagContent, index) => {
              const i18nKey = `hashtag_${hashtagContent}`;
              return (
                <Hashtag
                  key={hashtagContent}
                  to={`/hashtag/${hashtagContent}/${searchType.FLIX}`}
                  data-element_id={ButtonId.Home.ButtonTrendingHashtag}
                  data-tracking_payload={{
                    'discover.index': index + 1,
                    'discover.category': category,
                    'hashtag.content': hashtagContent,
                  }}
                >
                  <HashtagContent>
                    #
                    {i18n.customExists(i18nKey, {
                      ns: TranslationNamespace.SEARCH,
                    })
                      ? t(i18nKey, {
                          ns: TranslationNamespace.SEARCH,
                        })
                      : hashtagContent}
                  </HashtagContent>
                </Hashtag>
              );
            })}
          </ScrollItem>
        </InfiniteScroller>
      </Body>
    </StyledHashtagCategoryGrid>
  );
};

HashtagCategoryGrid.propTypes = {
  meId: PropTypes.string,
  category: PropTypes.string,
  nextPage: PropTypes.number,
  isNextPageFetching: PropTypes.bool,
  hashtags: PropTypes.array,
  i18n: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  fetchFeeds: PropTypes.func,
};

const StyledHashtagCategoryGrid = styled.div``;

const Header = styled.div`
  margin-bottom: 20px;
  ${media.mobile`
    margin-bottom: 16px;
  `};
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${textColor.white};
  ${media.mobile`
    font-size: 14px;
  `};
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(256px, 1fr));
  grid-gap: 12px;
  ${media.mobile`
    grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
    grid-gap: 8px;
  `};
`;

const Hashtag = styled(Link)`
  border-radius: 20px;
  padding: 0px 12px;
  height: 40px;
  min-width: 256px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral['100_alpha_10']};
  color: ${textColor.white};
  font-size: 18px;
  ${media.mobile`
    border-radius: 15px;
    height: 30px;
    min-width: 104px;
    font-size: 14px;
  `};
`;

const HashtagContent = styled.div`
  ${TextEllipsis};
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default withTranslation()(HashtagCategoryGrid);
